import React, { useState } from 'react';
import { Download, ExternalLink, FileText, Link2 } from 'lucide-react';
import type { FallGardenClubResource } from '../../types/fallGardenClub';

interface FallGardenClubResourceCardProps {
  resource: FallGardenClubResource;
}

export default function FallGardenClubResourceCard({ resource }: FallGardenClubResourceCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const isFile = resource.resource_type === 'file';
  const href = isFile ? resource.file_url : resource.external_url;

  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow group flex flex-col"
    >
      <div className="aspect-w-16 aspect-h-9 relative overflow-hidden">
        {resource.image_url ? (
          <>
            <div
              className={`absolute inset-0 bg-gray-100 transition-opacity duration-300 ${
                imageLoaded ? 'opacity-0' : 'opacity-100'
              }`}
            />
            <img
              src={resource.image_url}
              alt={resource.title}
              onLoad={() => setImageLoaded(true)}
              className={`w-full h-48 object-cover transition-opacity duration-300 group-hover:scale-105 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </>
        ) : (
          <div className="w-full h-48 flex items-center justify-center bg-primary-50">
            {isFile ? (
              <FileText className="h-12 w-12 text-primary-400" />
            ) : (
              <Link2 className="h-12 w-12 text-primary-400" />
            )}
          </div>
        )}
        {/* Type badge */}
        <div className="absolute top-2 right-2">
          <span className="bg-white/90 text-primary-700 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
            {isFile ? (
              <>
                <Download className="h-3 w-3" />
                File
              </>
            ) : (
              <>
                <ExternalLink className="h-3 w-3" />
                Link
              </>
            )}
          </span>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        {resource.category && (
          <span className="text-sm text-primary-600 font-medium mb-2">
            {resource.category.name}
          </span>
        )}
        <h3 className="font-semibold text-lg mb-2 group-hover:text-primary-600 transition-colors">
          {resource.title}
        </h3>
        {resource.description && (
          <p className="text-gray-600 text-sm line-clamp-2 mb-4">{resource.description}</p>
        )}
        <div className="mt-auto flex items-center gap-2 text-primary-600 text-sm font-medium">
          {isFile ? (
            <>
              <Download className="h-4 w-4" />
              <span>Download</span>
            </>
          ) : (
            <>
              <ExternalLink className="h-4 w-4" />
              <span>Visit Link</span>
            </>
          )}
        </div>
      </div>
    </a>
  );
}
